import { getFreshToken } from '@/lib/firebase';
import { intlAllRequestNo } from '@staff-app/shared-types';

const API_BASE = (import.meta.env.VITE_GRAPHQL_URL as string).replace(/\/graphql\/?$/, '');

/**
 * Download an AIR quote's PDF — `GET /api/intl-all-requests/:id/quote.pdf` (ROOT + SUPPORT). The
 * same REST shape as International Requests' `downloadIntlQuotePdf`: bytes cannot ride GraphQL, and
 * the server's own refusal message is shown rather than a bare status code.
 */
export async function downloadAirQuotePdf(requestId: number): Promise<void> {
  const { token } = await getFreshToken();
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/intl-all-requests/${requestId}/quote.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new Error('Could not reach the staff API — the request was rejected before it arrived (network or API gateway).');
  }
  if (!res.ok) {
    let msg = `Could not download the quote (HTTP ${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      const m = Array.isArray(body.message) ? body.message.join(', ') : body.message;
      if (m) msg = m;
    } catch {
      // Not JSON — keep the status line.
    }
    throw new Error(msg);
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const filename = /filename="([^"]+)"/.exec(cd)?.[1] || `optisigns-quote-${intlAllRequestNo(requestId)}.pdf`;
  const url = URL.createObjectURL(await res.blob());
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
